import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { authService } from '../services/authService';
import { registerMobilePush, unregisterMobilePush } from '../services/mobilePushService';

const AuthContext = createContext({});

const AUTH_STATE = {
  INITIALIZING: 'initializing',
  AUTHENTICATED: 'authenticated',
  UNAUTHENTICATED: 'unauthenticated',
  ERROR: 'error',
};

// مفتاح cache البروفايل في localStorage
const PROFILE_CACHE_KEY = 'msh_up_v2';

// وقت انتظار جلب البروفايل من الشبكة (بالخلفية)
const PROFILE_FETCH_TIMEOUT_MS = 6000;

// وقت انتظار bootstrap فقط للمستخدمين الجدد (لا يوجد cache)
const BOOTSTRAP_TIMEOUT_MS = 10000;

// ── أدوات cache البروفايل ──────────────────────────────────────────────────────
const readCachedProfile = () => {
  try {
    const raw = localStorage.getItem(PROFILE_CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeCachedProfile = (profile) => {
  try {
    if (profile) {
      localStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify(profile));
    } else {
      localStorage.removeItem(PROFILE_CACHE_KEY);
    }
  } catch {}
};

// ──────────────────────────────────────────────────────────────────────────────

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const authTestMode = String(import.meta.env?.VITE_AUTH_TEST_MODE || '').toLowerCase() === 'true';

  // ── قراءة cache فورية من localStorage (synchronous, لا شبكة) ──────────────
  const cachedProfileOnMount = authTestMode ? null : readCachedProfile();

  // SAFE: عند وجود cache بروفايل، نُعيّن user من الـ cache حتى لا يُعيد AdminGuard التوجيه
  // (user يأتي لاحقاً من INITIAL_SESSION، لكن قد يتأخر — فبدونه يُسجّل خروجاً عند فتح صفحات مثل المصورين/الحجوزات)
  const initialUserFromCache = cachedProfileOnMount
    ? { id: cachedProfileOnMount.id, email: cachedProfileOnMount.email ?? '' }
    : null;

  const [user, setUser] = useState(
    authTestMode ? { id: 'test-user-id', email: 'test@example.com' } : initialUserFromCache
  );
  const [userProfile, setUserProfile] = useState(
    authTestMode
      ? { id: 'test-user-id', role: 'customer', full_name: 'مستخدم تجريبي', email: 'test@example.com' }
      : cachedProfileOnMount // ← فوري من localStorage
  );
  // إذا كان هناك cache → AUTHENTICATED مباشرة (بدون loading)
  // إذا لا cache → INITIALIZING حتى نعرف الوضع
  const [authState, setAuthState] = useState(
    authTestMode
      ? AUTH_STATE.AUTHENTICATED
      : cachedProfileOnMount
        ? AUTH_STATE.AUTHENTICATED
        : AUTH_STATE.INITIALIZING
  );
  const [authErrorMessage, setAuthErrorMessage] = useState(null);
  const [session, setSession] = useState(null);
  // false until INITIAL_SESSION / getSession confirms JWT (prevents cache-only "authenticated" races)
  const [sessionReady, setSessionReady] = useState(authTestMode);

  const loading = authState === AUTH_STATE.INITIALIZING || !sessionReady;

  const bootstrapTimeoutRef = useRef(null);
  const initialSessionHandledRef = useRef(false);
  const mountedRef = useRef(true);
  const authStateRef = useRef(authState);
  const userProfileRef = useRef(userProfile);
  const explicitSignOutRef = useRef(false);
  const explicitProfileSetRef = useRef(false);
  // يُحدَّد من AdminLogin قبل signInWithPassword — نتخطى جلب البروفايل في applySession
  const skipNextProfileFetchRef = useRef(false);
  const sessionRef = useRef(session);
  const sessionReadyRef = useRef(sessionReady);

  authStateRef.current = authState;
  userProfileRef.current = userProfile;
  sessionRef.current = session;
  sessionReadyRef.current = sessionReady;

  const clearAuthError = () => setAuthErrorMessage(null);

  const clearBootstrapTimeout = () => {
    if (bootstrapTimeoutRef.current) {
      clearTimeout(bootstrapTimeoutRef.current);
      bootstrapTimeoutRef.current = null;
    }
  };

  const getLoginPathForCurrentRoute = () => {
    if (typeof window === 'undefined' || !window.location?.pathname) return null;
    const path = window.location.pathname;
    if (path.includes('-login') || path.endsWith('/login')) return null;
    if (path.includes('photographer')) return '/photographer-login';
    if (path.includes('admin')) return '/admin';
    if (path.includes('customer') || path.includes('dashboard')) return '/customer-login';
    return null;
  };

  useEffect(() => {
    if (authTestMode) {
      setAuthState(AUTH_STATE.AUTHENTICATED);
      setSessionReady(true);
      return () => {};
    }

    mountedRef.current = true;

    /**
     * applySession: المحور الأساسي لتطبيق جلسة صالحة
     *
     * المبدأ: المستخدم يُعرَف فوراً من الـ cache، ثم يُحدَّث البروفايل في الخلفية.
     * لا نستدعي getUser() أبداً — session.user كافٍ (JWT يُتحقق محلياً).
     * لا نُسجّل خروجاً عند أي خطأ شبكة — فقط نحدّث البيانات.
     */
    const applySession = async (session) => {
      if (!session?.user || !mountedRef.current) return false;

      clearBootstrapTimeout();
      if (session.access_token) {
        setSession(session);
        setSessionReady(true);
      }
      const u = session.user;
      setUser(u);

      // قراءة cache البروفايل
      const cached = readCachedProfile();
      const cacheIsValid = cached && cached.id === u.id;

      if (cacheIsValid) {
        // ← المستخدم يُعرَف فوراً بدون أي طلب شبكة
        setUserProfile(cached);
        setAuthState(AUTH_STATE.AUTHENTICATED);
        setAuthErrorMessage(null);
        // تسجيل push token في الخلفية (non-blocking)
        registerMobilePush(u.id).catch(() => {});
      }

      // جلب البروفايل الحديث في الخلفية (لا يوقف التطبيق)
      // SAFE: عند تسجيل دخول المشرف نتخطى — البروفايل يأتي من setUserProfileFromLogin
      if (skipNextProfileFetchRef.current) {
        skipNextProfileFetchRef.current = false;
        if (!cacheIsValid) {
          setAuthState(AUTH_STATE.AUTHENTICATED);
          setAuthErrorMessage(null);
        }
        return true;
      }

      try {
        const fresh = await Promise.race([
          authService.getUserProfile(u.id).catch(() => null),
          new Promise((resolve) => setTimeout(() => resolve(null), PROFILE_FETCH_TIMEOUT_MS)),
        ]);

        if (!mountedRef.current) return true;

        if (fresh) {
          // SAFE: لا نستبدل بروفايل المشرف أبداً ببروفايل بدور آخر (يُسجّل خروجاً تلقائياً)
          if (userProfileRef.current?.role === 'admin' && fresh.role !== 'admin') {
            // لا نستبدل — نبقى على المشرف
          } else if (explicitProfileSetRef.current) {
            const explicitProfile = userProfileRef.current;
            explicitProfileSetRef.current = false;
            if (fresh.role === explicitProfile?.role) {
              writeCachedProfile(fresh);
              setUserProfile(fresh);
            }
          } else {
            writeCachedProfile(fresh);
            setUserProfile(fresh);
          }
        }

        // أول مرة (لا cache): نُعلن عن AUTHENTICATED بعد جلب البروفايل
        if (!cacheIsValid) {
          setAuthState(AUTH_STATE.AUTHENTICATED);
          setAuthErrorMessage(null);
          registerMobilePush(u.id).catch(() => {});
        }
      } catch {
        // فشل جلب البروفايل لا يُسجّل خروجاً أبداً
        if (!mountedRef.current) return true;
        if (!cacheIsValid) {
          setAuthState(AUTH_STATE.AUTHENTICATED);
          setAuthErrorMessage(null);
        }
      }

      return true;
    };

    const setUnauthenticated = () => {
      clearBootstrapTimeout();
      writeCachedProfile(null);
      setSession(null);
      setSessionReady(true);
      setUser(null);
      setUserProfile(null);
      setAuthState(AUTH_STATE.UNAUTHENTICATED);
    };

    // Safety net: إذا INITIAL_SESSION لم يصل خلال BOOTSTRAP_TIMEOUT_MS
    // (نادر جداً — فقط إذا تعطل Supabase client كلياً)
    if (!cachedProfileOnMount) {
      // نُشغّل timeout فقط إذا لم يكن هناك cache (مستخدم جديد)
      bootstrapTimeoutRef.current = setTimeout(async () => {
        if (!mountedRef.current || authStateRef.current !== AUTH_STATE.INITIALIZING) return;
        try {
          const { data: { session } } = await supabase.auth.getSession();
          if (!mountedRef.current) return;
          if (session?.user) {
            await applySession(session);
          } else {
            setUnauthenticated();
          }
        } catch {
          if (mountedRef.current) setUnauthenticated();
        }
      }, BOOTSTRAP_TIMEOUT_MS);
    }

    // Never await Supabase calls inside onAuthStateChange — the auth lock is held
    // for the whole callback and concurrent getSession/refresh/from() will deadlock or steal.
    const scheduleApplySession = (session) => {
      queueMicrotask(() => {
        if (!mountedRef.current) return;
        void applySession(session);
      });
    };

    // ── onAuthStateChange: مصدر الحقيقة الوحيد (sync handler only) ───────────
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!mountedRef.current) return;

      // INITIAL_SESSION: أول حدث عند فتح التطبيق
      if (event === 'INITIAL_SESSION') {
        if (initialSessionHandledRef.current) return;
        initialSessionHandledRef.current = true;
        clearBootstrapTimeout();

        if (session?.user) {
          scheduleApplySession(session);
        } else {
          // لا جلسة → نتحقق ما إذا كان هناك cache خاطئ
          if (cachedProfileOnMount) {
            // Cache موجود لكن لا جلسة → ننتظر قليلاً (قد تكون مشكلة تحميل)
            setTimeout(() => {
              if (!mountedRef.current) return;
              void (async () => {
                try {
                  const { data: { session: retrySession } } = await supabase.auth.getSession();
                  if (!mountedRef.current) return;
                  if (retrySession?.user) {
                    await applySession(retrySession);
                  } else {
                    setUnauthenticated();
                  }
                } catch {
                  if (mountedRef.current) {
                    clearBootstrapTimeout();
                  }
                }
              })();
            }, 1500);
          } else {
            setUnauthenticated();
          }
        }
        return;
      }

      // TOKEN_REFRESHED: تجديد ناجح للـ token
      if (event === 'TOKEN_REFRESHED') {
        if (session?.access_token) {
          setSession(session);
          setSessionReady(true);
        }
        if (session?.user?.id) {
          setUser(session.user);
          if (authStateRef.current !== AUTH_STATE.AUTHENTICATED) {
            clearBootstrapTimeout();
            setAuthState(AUTH_STATE.AUTHENTICATED);
            setAuthErrorMessage(null);
          }
        }
        return;
      }

      // SIGNED_IN: تسجيل دخول ناجح (بعد OTP أو magic link)
      if (event === 'SIGNED_IN') {
        if (session?.user) {
          scheduleApplySession(session);
        }
        return;
      }

      // USER_UPDATED: تحديث بيانات المستخدم
      if (event === 'USER_UPDATED' && session?.user) {
        setUser(session.user);
        return;
      }

      // SIGNED_OUT: نتجاهله إلا إذا كان صريحاً من المستخدم
      if (event === 'SIGNED_OUT') {
        if (explicitSignOutRef.current) {
          explicitSignOutRef.current = false;
          setUnauthenticated();
        }
        // أي SIGNED_OUT آخر (انقطاع شبكة، خلفية Capacitor) → نتجاهله تماماً
        return;
      }
    });

    // ── استعادة الجلسة عند عودة التطبيق للمقدمة ───────────────────────────
    // نستخدم startAutoRefresh/stopAutoRefresh بدلاً من getSession() مباشرة
    // لتجنب التزاحم على Web Lock مع autoRefreshToken الداخلي
    const handleVisibilityChange = () => {
      if (!mountedRef.current) return;
      if (document.visibilityState === 'visible') {
        supabase.auth.startAutoRefresh();
      } else {
        supabase.auth.stopAutoRefresh();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      mountedRef.current = false;
      clearBootstrapTimeout();
      subscription?.unsubscribe();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [authTestMode]);

  const signOut = async () => {
    if (authTestMode) return;
    explicitSignOutRef.current = true;
    unregisterMobilePush().catch(() => {});
    writeCachedProfile(null);
    try {
      await authService.signOut();
    } catch {}
    setSession(null);
    setSessionReady(true);
    setUser(null);
    setUserProfile(null);
    setAuthState(AUTH_STATE.UNAUTHENTICATED);
    setAuthErrorMessage(null);
    window.location.replace('/');
  };

  /** بعد تسجيل دخول المشرف — يزامن الجلسة والبروفايل فوراً دون انتظار onAuthStateChange */
  const syncAdminLogin = (nextSession, profile) => {
    if (!nextSession?.access_token || !profile || !mountedRef.current) return;
    initialSessionHandledRef.current = true;
    skipNextProfileFetchRef.current = true;
    explicitProfileSetRef.current = true;
    clearBootstrapTimeout();
    setSession(nextSession);
    setSessionReady(true);
    setUser(nextSession.user);
    writeCachedProfile(profile);
    userProfileRef.current = profile;
    authStateRef.current = AUTH_STATE.AUTHENTICATED;
    setUserProfile(profile);
    setAuthState(AUTH_STATE.AUTHENTICATED);
    setAuthErrorMessage(null);
  };

  const setUserProfileFromLogin = (profile) => {
    if (profile && mountedRef.current) {
      explicitProfileSetRef.current = true; // حماية من استبدال background fetch
      clearBootstrapTimeout();
      writeCachedProfile(profile);
      userProfileRef.current = profile;
      authStateRef.current = AUTH_STATE.AUTHENTICATED;
      setUserProfile(profile);
      setAuthState(AUTH_STATE.AUTHENTICATED);
      setAuthErrorMessage(null);
    }
  };

  const setSkipNextProfileFetch = () => {
    skipNextProfileFetchRef.current = true;
  };

  /** بعد OTP — انتظر حتى AuthContext وPhotographerRoute يتزامنان مع JWT والبروفايل */
  const waitForAuthenticatedProfile = useCallback(
    ({ role, timeoutMs = 10000 } = {}) =>
      new Promise((resolve, reject) => {
        const deadline = Date.now() + timeoutMs;
        const tick = () => {
          const profile = userProfileRef.current;
          if (
            sessionReadyRef.current &&
            authStateRef.current === AUTH_STATE.AUTHENTICATED &&
            sessionRef.current?.access_token &&
            profile?.id
          ) {
            if (role && profile.role !== role) {
              if (Date.now() >= deadline) {
                reject(new Error('هذا الحساب لا يملك الصلاحية المطلوبة.'));
                return;
              }
              requestAnimationFrame(tick);
              return;
            }
            resolve({ session: sessionRef.current, profile });
            return;
          }
          if (Date.now() >= deadline) {
            reject(new Error('تعذر تأكيد تسجيل الدخول. حاول مرة أخرى.'));
            return;
          }
          requestAnimationFrame(tick);
        };
        tick();
      }),
    []
  );

  const value = {
    user,
    userProfile,
    session,
    sessionReady,
    loading,
    authState,
    authStatus: authState,
    authErrorMessage,
    clearAuthError,
    signOut,
    syncAdminLogin,
    setUserProfileFromLogin,
    setSkipNextProfileFetch,
    waitForAuthenticatedProfile,
    authTestMode,
    AUTH_STATE,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      {authErrorMessage && (
        <div
          role="alert"
          className="fixed bottom-4 left-4 right-4 z-[9999] max-w-md mx-auto bg-slate-800 text-white px-4 py-3 rounded-xl shadow-lg flex items-center justify-between gap-3"
        >
          <span className="text-sm">{authErrorMessage}</span>
          <button
            type="button"
            onClick={clearAuthError}
            className="shrink-0 p-1 rounded-lg hover:bg-white/10 text-white"
            aria-label="إغلاق"
          >
            ×
          </button>
        </div>
      )}
    </AuthContext.Provider>
  );
};
